import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateToDashboardFamilyLineHead, getDashboardFamilyLineNavigationMemberId } from "../../../Utils/dashboardFamilyLineNavigation";
import { isDashboardInLawMember } from "../../../Utils/dashboardMemberDisplayName";
import { isDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import { blockFamilyTreeNavigationOnMobile } from "../../Shared/FeatureNoticeModal";

export const getDashboardMemberPhotoUrl = (member, field = "image") => {
  const url = member?.[field];
  if (typeof url === "string" && url.trim()) {
    return url.trim();
  }
  return null;
};

/**
 * Tapping a name shows that person and no one else. Only rows that are themselves
 * about a couple pair two photos — see getAnniversaryPersonPhotoUrls and
 * getDashboardInRelationshipPhotoUrls.
 */
export function getDashboardMemberNamePhotoUrls(member) {
  return { imageUrl: getDashboardMemberPhotoUrl(member), secondaryImageUrl: null };
}


/** Partner photo for an in-law row: the descendant they are paired with. */
const IN_RELATIONSHIP_PARTNER_IMAGE_FIELDS = [
  "descendants_image",
  "descendantsImage",
  "descendant_image",
  "descendantImage",
  "spouse_image",
  "spouseImage",
];

export function getDashboardInRelationshipPartnerPhotoUrl(member) {
  for (const field of IN_RELATIONSHIP_PARTNER_IMAGE_FIELDS) {
    const url = getDashboardMemberPhotoUrl(member, field);
    if (url) return url;
  }
  return null;
}

/**
 * In Relationship rows name the in-law with their descendant partner in parentheses,
 * so the row is about the two of them: tapping the name shows both photos, in-law
 * first — the row leads with the in-law, so the photos follow the same order.
 */
export function getDashboardInRelationshipPhotoUrls(member) {
  const inLawImage = getDashboardMemberPhotoUrl(member, "image");
  const descendantImage = getDashboardInRelationshipPartnerPhotoUrl(member);

  if (inLawImage && descendantImage) {
    return {
      imageUrl: inLawImage,
      secondaryImageUrl: descendantImage,
      primaryLabel: "In-law",
      secondaryLabel: "Descendant",
    };
  }

  return { imageUrl: inLawImage || descendantImage, secondaryImageUrl: null };
}


/**
 * Anniversary rows: `image` = descendant, `spouse_image` = in-law (when both exist).
 * Clicking either name shows both photos when available.
 */
export function getAnniversaryPersonPhotoUrls(record) {
  const primaryImage = getDashboardMemberPhotoUrl(record, "image");
  const spouseImage = getDashboardMemberPhotoUrl(record, "spouse_image");

  if (!primaryImage && !spouseImage) {
    return { imageUrl: null, secondaryImageUrl: null };
  }

  const primaryIsInLaw =
    isDashboardInLawMember(record) && !isDashboardStartOrTopOfLineMember(record);

  if (primaryImage && spouseImage) {
    const descendantImage = primaryIsInLaw ? spouseImage : primaryImage;
    const inLawImage = primaryIsInLaw ? primaryImage : spouseImage;
    return {
      imageUrl: descendantImage,
      secondaryImageUrl: inLawImage,
      primaryLabel: "Descendant",
      secondaryLabel: "In-law",
    };
  }

  return { imageUrl: primaryImage || spouseImage, secondaryImageUrl: null };
}

export function DashboardPhotoModal({ photos, onClose }) {
  const imageUrl = photos?.imageUrl;
  const secondaryImageUrl = photos?.secondaryImageUrl;
  if (!imageUrl && !secondaryImageUrl) return null;

  const hasDualPhotos = Boolean(imageUrl && secondaryImageUrl);
  const primaryLabel = photos?.primaryLabel ?? "In-law";
  const secondaryLabel = photos?.secondaryLabel ?? "Descendant";

  return (
    <div
      className="dashboard-photo-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`dashboard-photo-modal-content${hasDualPhotos ? " has-dual-photos" : ""}`}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="dashboard-photo-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <div
          className={
            hasDualPhotos
              ? "dashboard-photo-modal-images-row"
              : "dashboard-photo-modal-image-wrap"
          }
        >
          {imageUrl ? (
            <div className={hasDualPhotos ? "dashboard-photo-modal-photo-col" : undefined}>
              <div className="dashboard-photo-modal-image-wrap">
                <img src={imageUrl} alt={primaryLabel} className="dashboard-photo-modal-image" />
              </div>
              {hasDualPhotos ? (
                <span className="dashboard-photo-modal-label">{primaryLabel}</span>
              ) : null}
            </div>
          ) : null}
          {secondaryImageUrl ? (
            <div className={hasDualPhotos ? "dashboard-photo-modal-photo-col" : undefined}>
              <div className="dashboard-photo-modal-image-wrap">
                <img
                  src={secondaryImageUrl}
                  alt={secondaryLabel}
                  className="dashboard-photo-modal-image"
                />
              </div>
              {hasDualPhotos ? (
                <span className="dashboard-photo-modal-label">{secondaryLabel}</span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function useDashboardPhotoModal() {
  const [photos, setPhotos] = useState(null);
  const openPhoto = useCallback((urlOrPhotos) => {
    if (!urlOrPhotos) return;
    if (typeof urlOrPhotos === "string") {
      setPhotos({ imageUrl: urlOrPhotos, secondaryImageUrl: null });
      return;
    }
    const { imageUrl, secondaryImageUrl, primaryLabel, secondaryLabel } = urlOrPhotos;
    if (imageUrl || secondaryImageUrl) {
      setPhotos({
        imageUrl: imageUrl || null,
        secondaryImageUrl: secondaryImageUrl || null,
        primaryLabel,
        secondaryLabel,
      });
    }
  }, []);
  const closePhoto = useCallback(() => setPhotos(null), []);

  return { photos, openPhoto, closePhoto };
}

export function DashboardPhotoName({
  imageUrl,
  secondaryImageUrl,
  primaryLabel,
  secondaryLabel,
  onViewPhoto,
  className = "",
  style,
  children,
}) {
  const hasPhoto = Boolean(imageUrl || secondaryImageUrl);

  const handleViewPhoto = () => {
    if (secondaryImageUrl) {
      onViewPhoto({ imageUrl, secondaryImageUrl, primaryLabel, secondaryLabel });
      return;
    }
    if (imageUrl) {
      onViewPhoto(imageUrl);
    }
  };

  return (
    <span
      className={`dashboard-photo-name${hasPhoto ? " is-clickable" : ""}${
        className ? ` ${className}` : ""
      }`}
      style={style}
      title={hasPhoto ? "View photo" : undefined}
      onClick={hasPhoto ? handleViewPhoto : undefined}
      onKeyDown={
        hasPhoto
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleViewPhoto();
              }
            }
          : undefined
      }
      role={hasPhoto ? "button" : undefined}
      tabIndex={hasPhoto ? 0 : undefined}
    >
      {children}
    </span>
  );
}

/** `afterBaseName` lands between the name and the "(Spouse)" — e.g. an angel for the in-law alone. */
export function DashboardMemberNameLabel({ baseName, descendantSpouseName, afterBaseName = null }) {
  return (
    <>
      {baseName}
      {afterBaseName}
      {descendantSpouseName ? (
        <>
          {" ("}
          <strong className="dashboard-descendant-spouse-name">{descendantSpouseName}</strong>
          {")"}
        </>
      ) : null}
    </>
  );
}

export function DashboardInLawAsterisk({ className = "", isLargeView = false }) {
  return (
    <span
      className={`dashboard-in-law-asterisk${className ? ` ${className}` : ""}${
        isLargeView ? " large-view" : ""
      }`}
    >
      *
    </span>
  );
}

/** Angel marker shown after a deceased member's name. */
export function DashboardDeceasedAngel({ className = "", isLargeView = false, size }) {
  const iconSize = size || (isLargeView ? "18px" : "12px");
  return (
    <span
      className={`dashboard-detail-angel-icon${className ? ` ${className}` : ""}${
        isLargeView ? " large-view" : ""
      }`}
    >
      <img
        src={window.getPublicUrl("/assets/images/angel.png")}
        alt="deceased"
        style={{ width: iconSize, height: iconSize }}
      />
    </span>
  );
}

/** Groups name + in-law *, deceased icon, and multiple-birth tag with no gaps between them. */
export function DashboardNameMarkerGroup({ className = "", children }) {
  return (
    <span className={`dashboard-name-marker-group${className ? ` ${className}` : ""}`}>
      {children}
    </span>
  );
}

// The Brown line (formerly Copper) has a hex that reads as orange beside the
// Orange line, so it is painted brown wherever a dashboard list names a line.
// Lines saved before the rename still carry the name Copper.
const COPPER_LINE_DISPLAY_HEX = "#8B4513";
// Gold moved from #FFC000 to #D6A953; lines saved earlier still carry the old hex.
const GOLD_LINE_DISPLAY_HEX = "#D6A953";

export function getDashboardLineDisplayHex(hexCode, colorName) {
  const hex = hexCode || "#000";
  const normalizedHex = String(hexCode || "").trim().toLowerCase();
  if (/^\s*(copper|brown)\b/i.test(colorName || "") || normalizedHex === "#cf6e1f") {
    return COPPER_LINE_DISPLAY_HEX;
  }
  if (/^\s*gold\s*$/i.test(colorName || "") || normalizedHex === "#ffc000") {
    return GOLD_LINE_DISPLAY_HEX;
  }
  return hex;
}

export function DashboardFamilyLineColorLink({
  hexCode,
  colorName,
  navigationMemberId,
  member,
  headsMap,
  className = "",
  dotSize,
  dotMarginLeft,
  dotMarginRight,
  showDot = true,
  title,
}) {
  const navigate = useNavigate();
  const hex = getDashboardLineDisplayHex(hexCode, colorName);
  const lineLabel = colorName || "";
  const resolvedId =
    navigationMemberId ??
    (member ? getDashboardFamilyLineNavigationMemberId(member, headsMap) : null);

  const handleClick = (event) => {
    event.stopPropagation();
    // Phones get the "tree is being revised" notice instead of the unusable canvas
    if (blockFamilyTreeNavigationOnMobile()) return;
    if (resolvedId) {
      navigate(`/family-tree?memberId=${resolvedId}`);
      return;
    }
    if (member) {
      navigateToDashboardFamilyLineHead(navigate, member, headsMap);
    }
  };

  if (!lineLabel) return null;

  const dotStyle = {
    width: dotSize || "7px",
    height: dotSize || "7px",
    marginLeft: dotMarginLeft,
    marginRight: dotMarginRight ?? "0.5em",
    backgroundColor: hex,
  };

  return (
    <button
      type="button"
      className={`heads-of-line-color-link${className ? ` ${className}` : ""}`}
      onClick={handleClick}
      title={title || `View ${lineLabel} in Family Tree`}
      style={{ color: hex }}
    >
      {showDot ? <span className="heads-of-line-color-dot" style={dotStyle} /> : null}
      <span className="heads-of-line-color-link-label" style={{ color: hex }}>
        {lineLabel}
      </span>
    </button>
  );
}

/** The head-of-line label never shows parentheses: "Mary Frances (" → "Mary Frances". */
function stripParens(value) {
  return String(value ?? "")
    .replace(/[()]/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export function DashboardPhotoHeadLine({
  name,
  hexCode,
  colorName,
  imageUrl,
  onViewPhoto,
  nameClassName = "",
  navigationMemberId,
  member,
  headsMap,
  separator = "",
}) {
  // Some head records carry a "(" in their name fields, which printed as
  // "Mary Frances (" before the line colour.
  const headName = stripParens(name);
  if (!headName) return null;

  const hex = getDashboardLineDisplayHex(hexCode, colorName);
  const color = colorName || "";
  const resolvedNavigationId =
    navigationMemberId ??
    (member ? getDashboardFamilyLineNavigationMemberId(member, headsMap) : null);

  return (
    <span className="dashboard-photo-head-line">
      <DashboardPhotoName
        imageUrl={imageUrl}
        onViewPhoto={onViewPhoto}
        className={nameClassName}
        style={{ color: hex }}
      >
        {headName}
      </DashboardPhotoName>
      {separator && color ? (
        <span className="dashboard-photo-head-line-separator" style={{ color: hex }}>
          {separator}
        </span>
      ) : null}
      {color ? (
        <>
          <span
            className="heads-of-line-color-dot dashboard-photo-head-line-dot"
            style={{ backgroundColor: hex }}
            aria-hidden="true"
          />
          <DashboardFamilyLineColorLink
            hexCode={hex}
            colorName={color}
            navigationMemberId={resolvedNavigationId}
            member={member}
            headsMap={headsMap}
            showDot={false}
          />
        </>
      ) : null}
    </span>
  );
}
